<template>
    <section class="mobile-search-page">
        <header class="mobile-search-header">
            <button type="button" aria-label="返回" @click="$emit('close')">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m15 5-7 7 7 7" />
                </svg>
            </button>
            <form class="mobile-search-header__form" @submit.prevent="submitSearch">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="10.75" cy="10.75" r="6.25" />
                    <path d="m15.5 15.5 4 4" />
                </svg>
                <input
                    ref="searchInput"
                    v-model.trim="keyword"
                    type="search"
                    autocomplete="off"
                    placeholder="搜索产品问题、教程、报错关键词"
                >
                <button v-if="keyword" type="button" class="mobile-search-header__clear" aria-label="清空" @click="clearKeyword">
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M5 5l6 6M11 5l-6 6" />
                    </svg>
                </button>
            </form>
            <button class="mobile-search-header__submit" type="button" @click="submitSearch">搜索</button>
        </header>

        <main class="mobile-search-content">
            <template v-if="!keyword">
                <div class="mobile-search-section-title">
                    <h1>搜索历史</h1>
                    <button v-if="history.length" type="button" @click="clearHistory">清空</button>
                </div>
                <div v-if="history.length" class="mobile-search-history">
                    <button v-for="item in history" :key="item" :title="item" type="button" @click="useHistory(item)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="8" />
                            <path d="M12 7.5V12l3 2" />
                        </svg>
                        {{ formatHistory(item) }}
                    </button>
                </div>
                <div v-else class="mobile-search-empty">
                    <div>搜</div>
                    <p>暂无搜索历史</p>
                </div>
            </template>

            <template v-else-if="mode === 'suggest'">
                <div class="mobile-search-section-title">
                    <h1>相关提示</h1>
                </div>
                <div v-if="loading" class="mobile-search-loading">正在搜索…</div>
                <div v-else-if="suggestions.length" class="mobile-search-suggestions">
                    <button v-for="item in suggestions" :key="item.answer_id" type="button" @click="selectSuggestion(item)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="10.75" cy="10.75" r="6.25" />
                            <path d="m15.5 15.5 4 4" />
                        </svg>
                        <span>
                            <strong>{{ item.title }}</strong>
                            <em>{{ typeTitle(item.type_id) }}</em>
                        </span>
                        <i>›</i>
                    </button>
                </div>
                <div v-else class="mobile-search-empty mobile-search-empty--small">
                    <p>没有匹配的提示，按“搜索”查看完整结果</p>
                </div>
            </template>

            <template v-else>
                <div class="mobile-search-section-title">
                    <div>
                        <h1>搜索结果</h1>
                        <p>“{{ submittedKeyword }}”相关内容</p>
                    </div>
                    <span v-if="total">{{ total }} 条</span>
                </div>
                <div v-if="loading" class="mobile-search-loading">正在加载搜索结果…</div>
                <div v-else-if="error" class="mobile-search-empty">
                    <div>!</div>
                    <p>搜索失败，请稍后重试</p>
                    <button type="button" @click="submitSearch">重新搜索</button>
                </div>
                <template v-else-if="results.length">
                    <mobile-question-card
                        v-for="item in results"
                        :key="item.answer_id"
                        :question="item"
                    ></mobile-question-card>
                    <button
                        v-if="hasMore"
                        class="mobile-search-more"
                        type="button"
                        :disabled="loadingMore"
                        @click="loadMore"
                    >
                        {{ loadingMore ? '正在加载…' : '加载更多' }}
                    </button>
                    <p v-else class="mobile-search-end">没有更多搜索结果了</p>
                </template>
                <div v-else class="mobile-search-empty">
                    <div>问</div>
                    <p>暂时没有找到相关答案</p>
                </div>
            </template>
        </main>
    </section>
</template>

<script>
import MobileQuestionCard from '@/components/mobile/wenda/question-card.vue';

const HISTORY_KEY = 'mobile_question_search_history';
const MAX_HISTORY_COUNT = 10;

export default {
    name: 'MobileQuestionSearch',
    components: {
        MobileQuestionCard,
    },
    data() {
        return {
            keyword: '',
            submittedKeyword: '',
            mode: 'suggest',
            history: [],
            suggestions: [],
            results: [],
            total: 0,
            pageNumber: 1,
            hasMore: false,
            loading: false,
            loadingMore: false,
            error: false,
            timer: null,
            requestNumber: 0,
        };
    },
    watch: {
        keyword(value) {
            clearTimeout(this.timer);
            if (!value) {
                this.mode = 'suggest';
                this.suggestions = [];
                return;
            }
            this.mode = 'suggest';
            this.timer = setTimeout(() => this.loadSuggestions(), 180);
        },
    },
    created() {
        this.loadHistory();
        document.body.style.overflow = 'hidden';
    },
    mounted() {
        this.$nextTick(() => {
            this.$refs.searchInput && this.$refs.searchInput.focus();
        });
    },
    beforeDestroy() {
        clearTimeout(this.timer);
        document.body.style.overflow = '';
    },
    methods: {
        loadHistory() {
            try {
                const data = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
                this.history = Array.isArray(data) ? data.slice(0, MAX_HISTORY_COUNT) : [];
            } catch (error) {
                this.history = [];
            }
        },
        saveHistory(value) {
            this.history = [value, ...this.history.filter((item) => item !== value)].slice(0, MAX_HISTORY_COUNT);
            localStorage.setItem(HISTORY_KEY, JSON.stringify(this.history));
        },
        clearHistory() {
            this.history = [];
            localStorage.removeItem(HISTORY_KEY);
        },
        clearKeyword() {
            this.keyword = '';
            this.results = [];
            this.total = 0;
            this.pageNumber = 1;
            this.hasMore = false;
            this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
        },
        useHistory(value) {
            this.keyword = value;
            this.$nextTick(() => this.submitSearch());
        },
        formatHistory(value) {
            const characters = Array.from(value || '');
            return characters.length > 10 ? `${characters.slice(0, 10).join('')}...` : value;
        },
        selectSuggestion(item) {
            this.keyword = item.title;
            this.$nextTick(() => this.submitSearch());
        },
        loadSuggestions() {
            if (!this.keyword) {
                return;
            }
            const currentRequest = ++this.requestNumber;
            this.loading = true;
            this.$bbs.get('/bbs/search/index', {
                params: {
                    search: {text: this.keyword},
                },
                need: {
                    progress: false,
                    loading: false,
                },
            }).then((response) => {
                if (currentRequest !== this.requestNumber || this.mode !== 'suggest') {
                    return;
                }
                this.suggestions = Array.isArray(response.data) ? response.data : [];
            }).catch(() => {
                if (currentRequest === this.requestNumber) {
                    this.suggestions = [];
                }
            }).finally(() => {
                if (currentRequest === this.requestNumber) {
                    this.loading = false;
                }
            });
        },
        submitSearch() {
            const value = this.keyword.trim();
            if (!value) {
                return;
            }
            clearTimeout(this.timer);
            this.requestNumber += 1;
            this.mode = 'result';
            this.submittedKeyword = value;
            this.pageNumber = 1;
            this.results = [];
            this.total = 0;
            this.hasMore = false;
            this.error = false;
            this.saveHistory(value);
            this.loadSearchResults(false);
        },
        loadSearchResults(append) {
            if (append) {
                this.loadingMore = true;
            } else {
                this.loading = true;
            }
            this.$bbs.get('/bbs/search/search', {
                params: {
                    pageNumber: this.pageNumber,
                    search: {text: this.submittedKeyword},
                },
                need: {
                    loading: false,
                },
            }).then((response) => {
                const payload = response.data || {};
                const list = Array.isArray(payload.data) ? payload.data : [];
                if (append) {
                    this.results.push(...list);
                } else {
                    this.results = list;
                }
                this.total = payload.total || this.results.length;
                this.hasMore = payload.total
                    ? this.results.length < payload.total
                    : list.length === 10;
            }).catch(() => {
                if (append) {
                    this.pageNumber -= 1;
                    this.$message.warning('下一页加载失败，请稍后重试');
                } else {
                    this.results = [];
                    this.total = 0;
                    this.error = true;
                }
            }).finally(() => {
                this.loading = false;
                this.loadingMore = false;
            });
        },
        loadMore() {
            if (this.loadingMore || !this.hasMore) {
                return;
            }
            this.pageNumber += 1;
            this.loadSearchResults(true);
        },
        typeTitle(typeId) {
            const mapping = {
                100: 'BIGEMAP PRO',
                200: 'BIGEMAP SERVER',
                300: 'BIGEMAP APP',
            };
            return mapping[typeId] || '产品问答';
        },
    },
};
</script>

<style scoped lang="less">
.mobile-search-page {
    position: fixed;
    z-index: 2000;
    inset: 0;
    overflow-y: auto;
    color: #172033;
    background: #f4f6f9;
}

.mobile-search-header {
    position: sticky;
    z-index: 2;
    top: 0;
    display: flex;
    height: 58px;
    padding: 0 10px;
    align-items: center;
    gap: 8px;
    background: #fff;
    border-bottom: 1px solid #e8ecf2;

    > button:first-child {
        display: flex;
        width: 34px;
        height: 40px;
        padding: 0;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        color: #435067;
        background: transparent;
        border: 0;

        svg {
            width: 23px;
            height: 23px;
            fill: none;
            stroke: currentColor;
            stroke-width: 1.9;
            stroke-linecap: round;
            stroke-linejoin: round;
        }
    }
}

.mobile-search-header__form {
    display: flex;
    height: 40px;
    min-width: 0;
    padding: 0 10px;
    flex: 1;
    align-items: center;
    gap: 7px;
    background: #f1f4f8;
    border: 1px solid transparent;
    border-radius: 7px;

    &:focus-within {
        background: #fff;
        border-color: #a9c8f8;
    }

    > svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        fill: none;
        stroke: #7c879a;
        stroke-width: 1.8;
        stroke-linecap: round;
    }

    input {
        width: 100%;
        min-width: 0;
        padding: 0;
        color: #202a3b;
        font-size: 14px;
        background: transparent;
        border: 0;
        outline: 0;

        &::-webkit-search-cancel-button,
        &::-webkit-search-decoration {
            -webkit-appearance: none;
            appearance: none;
        }
    }
}

.mobile-search-header__clear {
    display: flex;
    width: 22px;
    height: 22px;
    padding: 0;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: #aab2bf;
    border: 0;
    border-radius: 50%;

    svg {
        width: 14px;
        height: 14px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
        stroke-linecap: round;
    }
}

.mobile-search-header__submit {
    height: 40px;
    padding: 0 4px;
    flex-shrink: 0;
    color: #2478f2;
    font-size: 14px;
    font-weight: 500;
    background: transparent;
    border: 0;
}

.mobile-search-content {
    padding: 18px 12px 28px;
}

.mobile-search-section-title {
    display: flex;
    padding: 0 4px 12px;
    align-items: flex-end;
    justify-content: space-between;

    h1 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
    }

    p {
        margin: 3px 0 0;
        color: #98a2b3;
        font-size: 12px;
    }

    button,
    > span {
        padding: 0;
        color: #8c97a8;
        font-size: 12px;
        background: transparent;
        border: 0;
    }
}

.mobile-search-history {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;

    button {
        display: flex;
        max-width: 100%;
        height: 34px;
        padding: 0 12px;
        align-items: center;
        gap: 6px;
        overflow: hidden;
        color: #596579;
        font-size: 13px;
        text-overflow: ellipsis;
        white-space: nowrap;
        background: #fff;
        border: 1px solid #e4e9f0;
        border-radius: 17px;

        svg {
            width: 15px;
            height: 15px;
            flex-shrink: 0;
            fill: none;
            stroke: #8994a6;
            stroke-width: 1.7;
            stroke-linecap: round;
        }
    }
}

.mobile-search-suggestions {
    overflow: hidden;
    background: #fff;
    border: 1px solid #e8ecf2;
    border-radius: 8px;

    button {
        display: flex;
        width: 100%;
        min-height: 62px;
        padding: 10px 14px;
        align-items: center;
        gap: 10px;
        text-align: left;
        background: #fff;
        border: 0;
        border-bottom: 1px solid #edf0f4;

        &:last-child {
            border-bottom: 0;
        }

        > svg {
            width: 19px;
            height: 19px;
            flex-shrink: 0;
            fill: none;
            stroke: #7e8a9e;
            stroke-width: 1.7;
            stroke-linecap: round;
        }

        > span {
            display: flex;
            min-width: 0;
            flex: 1;
            flex-direction: column;

            strong {
                overflow: hidden;
                color: #253044;
                font-size: 14px;
                font-weight: 500;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            em {
                margin-top: 5px;
                color: #98a2b3;
                font-size: 11px;
                font-style: normal;
            }
        }

        > i {
            color: #a0a9b8;
            font-size: 22px;
            font-style: normal;
        }
    }
}

.mobile-search-loading,
.mobile-search-empty {
    display: flex;
    min-height: 250px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #909bad;
    font-size: 13px;

    > div {
        display: flex;
        width: 46px;
        height: 46px;
        align-items: center;
        justify-content: center;
        color: #2478f2;
        font-size: 19px;
        font-weight: 600;
        background: #eaf2ff;
        border-radius: 14px;
    }

    p {
        margin: 12px 0;
    }

    button {
        padding: 7px 14px;
        color: #2478f2;
        background: #edf4ff;
        border: 0;
        border-radius: 5px;
    }
}

.mobile-search-more {
    width: 100%;
    height: 44px;
    margin-top: 4px;
    color: #536177;
    font-size: 13px;
    background: #fff;
    border: 1px solid #e1e7ef;
    border-radius: 7px;

    &:disabled {
        color: #9ba5b4;
        background: #f8f9fb;
    }
}

.mobile-search-end {
    margin: 16px 0 0;
    color: #a0a9b8;
    font-size: 12px;
    text-align: center;
}

.mobile-search-empty--small {
    min-height: 160px;
}
</style>
